import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appHub", "children", "withoutSidebar", "onReady", "onContentReady", "onContentError", "iframeContainerID"],
  _excluded2 = ["handleMessage", "show"],
  _excluded3 = ["handleMessage", "show"],
  _excluded4 = ["handleMessage", "show"];
import I18n from 'I18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import CopilotProvider from 'copilot-toolkit/CopilotProvider';
import { IS_COMMENT_MODE_ACTIVE } from '../constants/CollaborationSidebarStateProperties';
import CollaborationSidebarStateContext from '../contexts/CollaborationSidebarStateContext';
import UserSettingsProvider from '../contexts/UserSettingsProvider';
import usePersistPropsToCollaborationSidebarState from '../hooks/usePersistPropsToCollaborationSidebarState';
import { showWarningNotification } from '../utils/notifications';
import { getIsCommentingUrl } from '../utils/urls';
import AsyncOnboardingTour from 'collaboration-sidebar/features/deepCommenting/components/AsyncOnboardingTour';
import CommentModeIndicator from 'collaboration-sidebar/features/deepCommenting/components/CommentModeIndicator';
import { CommentObjectType } from '../constants';
import CollaborationSidebarPropsContext from '../contexts/CollaborationSidebarPropsContext';
import useEmbeddedComments from '../features/deepCommenting/hooks/useEmbeddedComments';
import useHasUnreadComments from '../hooks/useHasUnreadComments';
import useSidebarIframeHandlers from '../hooks/useSidebarIframeHandlers';
import useConfirmDeleteCommentModal from 'collaboration-sidebar/hooks/modals/useConfirmDeleteCommentModal';
import useNotificationSettingsModal from 'collaboration-sidebar/hooks/modals/useNotificationSettingsModal';
import useTaskDeleteModal from 'collaboration-sidebar/hooks/modals/useTaskDeleteModal';
import { SUPPORTED_HUBS } from 'collaboration-sidebar/constants/supportedHubs';
import useSidebarOpenCloseHandlers from '../hooks/useSidebarOpenCloseHandlers';
import useHasSidebarPermission from '../hooks/useHasSidebarPermission';
import AsyncMessageHandlers from './MessageHandlers/AsyncMessageHandlers';
import Modals from './Modals';
import EmbeddedCommentSetup from './EmbeddedCommentSetup';
import usePollForCommentsChanges from '../hooks/usePollForCommentsChanges';
import useOnboardingTour from '../hooks/useOnboardingTour';
import { COMMENT_ONBOARDING_TOUR_ID } from '../constants/tourIDs';
import { useCollaborationSidebarState } from '../hooks';
import useModuleScrollAndHighlight from '../features/deepCommenting/hooks/useModuleScrollAndHighlight';
import { UsageTracker } from '../utils/usageTracker';
import useGetPropsFromFAS from '../features/fas/hooks/useGetPropsFromFAS';
import useStableReferences from '../hooks/useStableReferences';
import UserInfoProvider from './UserInfoProvider';
import ErrorCountWrapper from './errors/ErrorCountWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function ControlledCollaborationSidebarProvider(_ref) {
  var _fasProps2;
  let {
      appHub: appHubProp,
      children,
      withoutSidebar: withoutSidebarProp,
      onReady,
      onContentReady,
      onContentError,
      iframeContainerID
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = useCollaborationSidebarState();

  /**
   * Keep backwards compatibility by providing default values for FAS props when not ungated or when FAS is available.
   */
  const defaultFasProps = useMemo(() => ({
    appHub: appHubProp && Array.isArray(appHubProp) ? appHubProp : appHubProp && [SUPPORTED_HUBS[appHubProp]],
    defaultSidebarPlacement: rest.defaultSidebarPlacement,
    dragAndDropAreas: rest.dragAndDropAreas,
    enabledFeatures: rest.enabledFeatures,
    hideCommentModeAndToggle: rest.hideCommentModeAndToggle,
    isListingPage: rest.isListingPage,
    isOverlay: rest.isOverlay,
    objectType: undefined,
    scrollablePage: rest.scrollablePage,
    showSidebarHeader: rest.showSidebarHeader,
    withoutSidebar: withoutSidebarProp
  }), [appHubProp, rest.defaultSidebarPlacement, rest.dragAndDropAreas, rest.enabledFeatures, rest.hideCommentModeAndToggle, rest.isListingPage, rest.isOverlay, rest.scrollablePage, rest.showSidebarHeader, withoutSidebarProp]);
  let fasProps = defaultFasProps;

  /**
   * Read from Framework App Settings.
   * If not available, fallback to provided props.
   */
  const {
    fasProps: tempProps,
    loading
  } = useGetPropsFromFAS({
    appName: rest.appName,
    enabledFeatures: rest.enabledFeatures
  });
  if (tempProps) {
    fasProps = tempProps;
  }
  const didEmitOnReady = useRef(false);
  const hasShownNoPermissionWarning = useRef(false);
  const pageContentRef = useRef(null);
  const sendMessageRef = useRef(undefined);
  const onMessageRef = useRef(() => {});
  const [messageHandlersReady, setMessageHandlersReady] = useState(false);

  /**
   * Tracks all usages of collaboration-sidebar by app name and object type
   */
  useEffect(() => {
    var _fasProps;
    UsageTracker.track('collabSidebarInteraction', {
      action: 'render collaboration-sidebar',
      applicationName: rest.appName,
      objectType: (_fasProps = fasProps) === null || _fasProps === void 0 ? void 0 : _fasProps.objectType
    });
  }, [(_fasProps2 = fasProps) === null || _fasProps2 === void 0 ? void 0 : _fasProps2.objectType, rest.appName]);
  useEffect(() => {
    if (messageHandlersReady && onReady && !didEmitOnReady.current) {
      didEmitOnReady.current = true;
      onReady();
    }
  }, [messageHandlersReady, onReady]);
  const {
    objectId,
    openLeftEditor,
    closeLeftEditor,
    onCommentsUpdated,
    embeddedCommentsProps,
    commentingDisabled,
    onScrollToModule,
    onHighLightModuleChange
  } = rest;
  const expandableSidebarRef = useRef(null);
  const iframeHandlerProps = useSidebarIframeHandlers({
    enabledFeatures: fasProps.enabledFeatures,
    isOpen: collaborationSidebarState.isOpen,
    onContentError,
    onContentReady,
    sendMessageRef,
    withoutSidebar: fasProps.withoutSidebar
  });
  const hasSidebarPermission = useHasSidebarPermission({
    appHub: fasProps.appHub,
    enabledFeatures: fasProps.enabledFeatures
  });
  useEffect(() => {
    if (!hasShownNoPermissionWarning.current && !hasSidebarPermission && getIsCommentingUrl() && !loading) {
      showWarningNotification({
        title: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingTitle'),
        message: I18n.text('collaborationSidebar.notifications.failure.missingPermissionCommentingBody'),
        sticky: true
      });
      hasShownNoPermissionWarning.current = true;
    }
  }, [hasSidebarPermission, loading]);
  const {
    hasUnreadComments,
    unreadCommentsCount,
    markCommentAsRead,
    refetchComments
  } = useHasUnreadComments(loading ? undefined : fasProps.objectType, objectId);
  const {
    isCommentModeActive
  } = collaborationSidebarState;
  usePollForCommentsChanges({
    objectId,
    objectType: fasProps.objectType,
    onUpdateCollaborationSidebarState,
    hasSidebarPermission,
    isOpen: collaborationSidebarState.isOpen,
    assetsLastUpdated: collaborationSidebarState.assetsLastUpdated
  });
  const {
    handleClose
  } = useSidebarOpenCloseHandlers({
    onUpdateCollaborationSidebarState,
    openLeftEditor,
    closeLeftEditor
  });
  const {
    hideEmbeddedComments,
    setHideEmbeddedComments,
    setModuleReply,
    removeModuleReply,
    startCommentMode,
    cancelCommentMode
  } = useEmbeddedComments({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    navigateTo: embeddedCommentsProps && embeddedCommentsProps.indicatorNavigateTo
  });
  const _useConfirmDeleteComm = useConfirmDeleteCommentModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleConfirmDeleteCommentModalMessage,
      show: showDeleteCommentModal
    } = _useConfirmDeleteComm,
    confirmDeleteCommentModalProps = _objectWithoutPropertiesLoose(_useConfirmDeleteComm, _excluded2);
  const _useNotificationSetti = useNotificationSettingsModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleNotificationSettingsModalMessage,
      show: showNotificationSettingsModal
    } = _useNotificationSetti,
    notificationSettingsModalProps = _objectWithoutPropertiesLoose(_useNotificationSetti, _excluded3);
  const _useTaskDeleteModal = useTaskDeleteModal({
      sendMessageRef,
      isSendMessageReady: iframeHandlerProps.isReady
    }),
    {
      handleMessage: handleTaskUpdatePanelMessage,
      show: showTaskDeleteModal
    } = _useTaskDeleteModal,
    taskDeleteModalProps = _objectWithoutPropertiesLoose(_useTaskDeleteModal, _excluded4);
  const showCommentingTour = useOnboardingTour({
    objectType: fasProps.objectType,
    tourId: COMMENT_ONBOARDING_TOUR_ID,
    featureIsVisible: fasProps.objectType === CommentObjectType.COMMENTING_SANDBOX && !commentingDisabled && !collaborationSidebarState.isOpen
  });
  useModuleScrollAndHighlight({
    collaborationSidebarState,
    onScrollToModule,
    onHighLightModuleChange
  });
  const [isPageContentRefSet, setIsPageContentRefSet] = useState(false);
  const collaborationSidebarProviderProps = Object.assign({}, rest, {
    pageContentRef,
    isPageContentRefSet,
    setIsPageContentRefSet,
    hasSidebarPermission,
    expandableSidebarRef,
    hasUnreadComments,
    unreadCommentsCount,
    isModalOpen: showDeleteCommentModal || showTaskDeleteModal,
    iframe: iframeHandlerProps,
    onMessageRef,
    messageHandlersReady,
    startCommentMode,
    cancelCommentMode
  }, fasProps);
  return (
    /*#__PURE__*/
    // Creating a layer here makes sure that if the collaboration sidebar is
    // rendered in an iframe the copilot it uses will only be initialized for
    // the iframe context it's rendered in
    _jsxs(CopilotProvider, {
      createLayer: true,
      children: [/*#__PURE__*/_jsx(AsyncMessageHandlers, {
        collaborationSidebarState: collaborationSidebarState,
        onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState
        // @ts-expect-error ts-migrate(2322) FIXME: Type '({ payload }: $tsMigrateAny) => void' is not... Remove this comment to see the full error message
        ,
        handleTaskUpdatePanelMessage: handleTaskUpdatePanelMessage
        // @ts-expect-error ts-migrate(2322) FIXME: Type '({ payload }: $tsMigrateAny) => void' is not... Remove this comment to see the full error message
        ,
        handleConfirmDeleteCommentModalMessage: handleConfirmDeleteCommentModalMessage,
        handleNotificationSettingsModalMessage: handleNotificationSettingsModalMessage,
        markCommentAsRead: markCommentAsRead,
        handleClose: handleClose,
        refetchComments: refetchComments,
        onCommentsUpdated: onCommentsUpdated,
        preLoadIframe: iframeHandlerProps.preLoadIframe,
        setHideEmbeddedComments: setHideEmbeddedComments,
        hideEmbeddedComments: hideEmbeddedComments,
        sendMessageRef: sendMessageRef,
        setModuleReply: setModuleReply,
        removeModuleReply: removeModuleReply,
        onMessageRef: onMessageRef,
        setMessageHandlersReady: setMessageHandlersReady
      }), /*#__PURE__*/_jsx(CollaborationSidebarPropsContext.Provider, {
        value: collaborationSidebarProviderProps,
        children: /*#__PURE__*/_jsx(UserInfoProvider, {
          children: /*#__PURE__*/_jsxs(UserSettingsProvider, {
            objectId: objectId,
            children: [/*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(EmbeddedCommentSetup, {}), /*#__PURE__*/_jsx(Modals, {
                confirmDeleteCommentModalProps: confirmDeleteCommentModalProps,
                taskDeleteModalProps: taskDeleteModalProps,
                notificationSettingsModalProps: notificationSettingsModalProps,
                showDeleteCommentModal: showDeleteCommentModal,
                showNotificationSettingsModal: showNotificationSettingsModal,
                showTaskDeleteModal: showTaskDeleteModal,
                withoutSidebar: !!fasProps.withoutSidebar,
                isReady: messageHandlersReady,
                IFrameError: iframeHandlerProps.IFrameError,
                iframeContainerID: iframeContainerID
              })]
            }), children, showCommentingTour && /*#__PURE__*/_jsx(AsyncOnboardingTour, {
              tourId: COMMENT_ONBOARDING_TOUR_ID
            }), isCommentModeActive && /*#__PURE__*/_jsx(CommentModeIndicator, {
              onCancel: () => {
                onUpdateCollaborationSidebarState({
                  [IS_COMMENT_MODE_ACTIVE]: false
                });
                UsageTracker.track('collabSidebarInteraction', {
                  action: 'close comment mode'
                });
              }
            })]
          })
        })
      })]
    })
  );
}
const STABLE_REFERENCE_KEYS = ['enabledFeatures', 'appHub'];
function CollaborationSidebarProviderWithState(props) {
  const {
    collaborationSidebarState,
    onUpdateCollaborationSidebarState
  } = props;
  const stableProps = useStableReferences({
    object: props,
    keys: STABLE_REFERENCE_KEYS
  });
  const collaborationSidebarStateWithPersistedProps = usePersistPropsToCollaborationSidebarState({
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    collaborationSidebarProps: stableProps
  });
  return /*#__PURE__*/_jsx(CollaborationSidebarStateContext.Provider, {
    value: {
      collaborationSidebarState: collaborationSidebarStateWithPersistedProps,
      onUpdateCollaborationSidebarState
    },
    children: /*#__PURE__*/_jsx(ControlledCollaborationSidebarProvider, Object.assign({}, stableProps))
  });
}
export default function CollaborationSidebarProviderWithErrorCount(props) {
  return /*#__PURE__*/_jsx(ErrorCountWrapper, {
    appName: props.appName,
    errorBoundaryFingerprint: "CollaborationSidebarProvider",
    onUpdateCollaborationSidebarState: props.onUpdateCollaborationSidebarState,
    threshold: 2000,
    children: /*#__PURE__*/_jsx(CollaborationSidebarProviderWithState, Object.assign({}, props))
  });
}